import React, { useEffect, useState } from 'react';
import { AnnouncementBar } from '../components/AnnouncementBar';
import { SiteNav } from '../components/SiteNav';
import { Hero } from '../components/Hero';
import { BenefitStrip } from '../components/BenefitStrip';
import { ProblemSection } from '../components/ProblemSection';
import { LensSection } from '../components/LensSection';
import { PrescriptionSection } from '../components/PrescriptionSection';
import { UseCaseSection } from '../components/UseCaseSection';
import { StorySection } from '../components/StorySection';
import { DemoSection } from '../components/DemoSection';
import { DetailSection } from '../components/DetailSection';
import { SpecsSection } from '../components/SpecsSection';
import { OfferSection } from '../components/OfferSection';
import { GuaranteeSection } from '../components/GuaranteeSection';
import { AssuranceSection } from '../components/AssuranceSection';
import { ValueStackSection } from '../components/ValueStackSection';
import { SocialProofSection } from '../components/SocialProofSection';
import { FaqSection } from '../components/FaqSection';
import { FinalCta } from '../components/FinalCta';
import { DiscountSection } from '../components/DiscountSection';
import { SiteFooter } from '../components/SiteFooter';
import { StickyBuyBar } from '../components/StickyBuyBar';
import { ObjectionRecovery } from '../components/MiniFunnels';
import { ObjectionModal } from '../components/ObjectionModal';
import { useObjectionTrigger } from '../hooks/useObjectionTrigger';
import { trackCustom, packEvents } from '../utils/tracking';
import { onOfferRequest } from '../utils/offerBus';
import { Offer } from '../data/product';
import { ObjectionId, objectionChoices, RECOVERY_ID } from '../data/objections';
type OfferId = 'single' | 'trio' | 'crew';
interface HomeProps {
  showAnnouncementBar?: boolean;
  defaultOffer?: OfferId;
}
export function Home({
  showAnnouncementBar = true,
  defaultOffer = 'trio'
}: HomeProps) {
  const [selectedOffer, setSelectedOffer] = useState<OfferId>(defaultOffer);
  const [activeObjection, setActiveObjection] = useState<ObjectionId | null>(null);
  const objection = useObjectionTrigger();
  const handleSelectOffer = (id: Offer['id']) => {
    setSelectedOffer(id);
    trackCustom(packEvents[id].selected);
  };

  // CTAs lower on the page preselect the pack they argue for.
  useEffect(() => onOfferRequest(id => setSelectedOffer(id)), []);
  const handleObjection = (id: ObjectionId) => {
    const choice = objectionChoices.find(item => item.id === id);
    if (!choice) return;
    trackCustom('objection_selected', {
      objection: id
    });
    objection.close();
    // "Just looking" is respected: no funnel, no further interruption this session.
    if (id === 'looking') {
      trackCustom('objection_just_looking_selected');
      return;
    }
    setActiveObjection(id);
    if (choice.recommend && choice.recommend !== 'business') setSelectedOffer(choice.recommend);
    // Wait for the revealed funnel to mount before scrolling to it.
    window.setTimeout(() => {
      document.getElementById(RECOVERY_ID)?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };

  // "None of these. I want it." Take the 5% code and go straight to the packs.
  const handleWantIt = () => {
    trackCustom('objection_none_want_it');
    trackCustom('rescue_5_clicked');
    objection.close();
    window.setTimeout(() => {
      document.getElementById('offer')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }, 80);
  };
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1280" data-mp="3nukh612asmsr">
      {showAnnouncementBar && <AnnouncementBar data-id="element-1281" data-mp="5oflt4mqvhq1" />}
      <SiteNav data-id="element-1282" data-mp="5ljpfkqjp13p" />
      <main className="pb-24 lg:pb-0" data-id="element-1283" data-mp="ojgn22b9k8w">
        {/* 1. Promise + product */}
        <Hero data-id="element-1284" data-mp="qnvgf1r03ny" />
        {/* 2. Instant reassurance */}
        <BenefitStrip data-id="element-1285" data-mp="1h2077kpknttq" />
        {/* 3. Early credibility — 50,000+ users, 4.9/5 */}
        <SocialProofSection data-id="element-1286" data-mp="1c4855xv7qrbt" />
        {/* 4. Pain + head-to-head against the old way */}
        <ProblemSection data-id="element-1287" data-mp="27e4xogfsui92" />
        {/* 5. The transformation, made visual */}
        <DemoSection data-id="element-1288" data-mp="27fzhtsj8y6c3" />
        {/* 6. The differentiator: 3 lenses */}
        <LensSection data-id="element-1289" data-mp="2cdhvtxc4fbqy" />
        {/* 6b. Prescription ready today, RX Insert in development */}
        <PrescriptionSection data-id="element-1290" data-mp="2hb9pavsez5x9" />
        {/* 7. Rational justification */}
        <DetailSection data-id="element-1291" data-mp="2mbfjaim5p4ii" />
        {/* 7b. The questions people ask before buying */}
        <SpecsSection data-id="element-1292" data-mp="2qd0pjl46qkjq" />
        {/* 8. Self-identification: "that's my job" */}
        <UseCaseSection data-id="element-1293" data-mp="2qawnl8pujvm3" />
        {/* 8b. Who is behind the gear */}
        <StorySection data-id="element-1294" data-mp="2va9cdz0a3crb" />
        {/* 9. Risk removed before any price is shown */}
        <GuaranteeSection data-id="element-1295" data-mp="303lqhf7c015t" />
        {/* 9b. Shipping, returns and risk */}
        <AssuranceSection data-id="element-1296" data-mp="3526mf2fjvyog" />
        {/* 10. The one economic comparison: what it all costs separately */}
        <ValueStackSection data-id="element-1297" data-mp="3a1jatrm2ldt5" />
        {/* 11. The decision */}
        <OfferSection selectedOffer={selectedOffer} onSelectOffer={handleSelectOffer} data-id="element-1298" data-mp="39yx4fvax14ci" />
        {/* 12. Personalized recovery area: hidden until the visitor tells us what is holding them back */}
        <ObjectionRecovery active={activeObjection} onChooseAnother={objection.reopen} data-id="element-1299" data-mp="12cn9bbrzhmd2" />
        {/* 13. Last objections */}
        <FaqSection data-id="element-1300" data-mp="tfcwhlzipp61" />
        {/* 14. Close */}
        <FinalCta onSelectOffer={handleSelectOffer} data-id="element-1301" data-mp="tg4suz0xvqxd" />
        {/* 15. Last-chance code for anyone still on the fence */}
        <DiscountSection data-id="element-1302" data-mp="1m2pk7zhf030x" />
      </main>
      <SiteFooter data-id="element-1303" data-mp="1m1nz126oq05h" />
      <StickyBuyBar selectedOffer={selectedOffer} data-id="element-1304" data-mp="1m264mpf8lqq2" />
      <ObjectionModal open={objection.open} onClose={objection.close} onChoose={handleObjection} onWantIt={handleWantIt} data-id="element-1305" data-mp="1h3mdmalsdoqu" />
    </div>;
}